import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiCall } from "../Apis/apiCall";
import { useUser } from "../context/UserContext";
import LoadingComponent from "./ui/LoadingComponent";
import { handleApiResponse, handleCustomResponse } from "../utils/response";
import ErrorAlert from "./ui/ErrorAlert";

const ProfileLayer = () => {
  const { updateUserData } = useUser();
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [userData, setUserData] = useState({
    username: "",
    email: "",
    role_name: "",
    profile_picture: "",
  });
  const [loading, setLoading] = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataFetched, setDataFetched] = useState(false); // Track if data is already fetched
  const navigate = useNavigate();

  // Memoize the fetchUserData function to prevent unnecessary re-renders
  const fetchUserData = useCallback(async () => {
    // Prevent multiple API calls
    if (dataFetched) return;

    try {
      const userString = localStorage.getItem("user");
      const accessToken = localStorage.getItem("access_token");

      if (!userString || !accessToken) {
        navigate("/login");
        return;
      }

      const user = JSON.parse(userString);
      const userId = user?.user_id;

      if (!userId) {
        throw new Error("User ID not found in local storage");
      }

      const response = await fetch(
        `https://pevbackend.codovio.com/public/api/get-user-by-id/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch user data");
      }

      const data = await response.json();
      const profilePicture =
        data.data?.profile_picture || data.profile_picture || "";
      const rawRoleName =
        data.data?.role_name || data.data?.role?.role_name || "Unknown Role";
      const fetchedUserData = {
        username: data.data?.username || data.username || "",
        email: data.data?.email || data.email || "",
        role_name: rawRoleName.replace(/_/g, " "), // Convert underscores to spaces
        profile_picture: profilePicture,
      };

      setUserData(fetchedUserData);
      setDataFetched(true); // Mark data as fetched

      // Update UserContext only once
      updateUserData(fetchedUserData);

      if (profilePicture) {
        setImagePreview(
          `https://pevbackend.codovio.com/public/uploads/profile_pictures/${profilePicture}`
        );
      }
    } catch (err) {
      setError(err.message);
      // handleCustomResponse("error", "Error loading profile");
    } finally {
      setLoading(false);
    }
  }, [navigate, updateUserData, dataFetched]);

  // Fetch user data from API only once when component mounts
  useEffect(() => {
    fetchUserData();
  }, []); // Empty dependency array - runs only once

  // Handle image file selection and preview
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle username input with character limit validation
  const handleUsernameChange = (e) => {
    const value = e.target.value;
    if (value.length <= 50) {
      setUserData({ ...userData, username: value });
    } else {
      handleCustomResponse("warning", "Username cannot exceed 50 characters");
    }
  };

  // Handle image upload to API
  const handleImageUpload = async () => {
    if (!selectedFile) {
      return null;
    }

    try {
      const accessToken = localStorage.getItem("access_token");
      if (!accessToken) {
        throw new Error("No access token found");
      }

      const formData = new FormData();
      formData.append("profile_picture", selectedFile);

      const response = await fetch(
        "https://pevbackend.codovio.com/public/api/upload-image",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to upload image");
      }

      const data = await response.json();
      if (data.errorCode !== 0 || data.status !== "success") {
        throw new Error("Image upload failed");
      }

      handleCustomResponse("success", "Image uploaded successfully!");

      return { tiny_url: data.tiny_url, original: data.original };
    } catch (err) {
      handleCustomResponse("error", "Image upload failed");

      throw err;
    }
  };

  // Handle user data update
  const handleUpdateUser = async (e) => {
    e.preventDefault();

    // Validate username length before submission
    if (userData.username.length > 50) {
      handleCustomResponse("error", "Username cannot exceed 50 characters");

      return;
    }

    if (userData.username.trim().length === 0) {
      handleCustomResponse("error", "Username is required");

      return;
    }

    setUpdateLoading(true);
    setError(null);

    try {
      const userString = localStorage.getItem("user");
      const accessToken = localStorage.getItem("access_token");
      if (!userString || !accessToken) {
        navigate("/login");
        return;
      }

      const user = JSON.parse(userString);
      const userId = user?.user_id;

      if (!userId) {
        throw new Error("User ID not found");
      }

      let tinyUrl = userData.profile_picture;
      let imageUrl = imagePreview;
      if (selectedFile) {
        const uploadResult = await handleImageUpload();
        if (uploadResult) {
          tinyUrl = uploadResult.tiny_url;
          imageUrl = uploadResult.original;
        }
      }

      const updateData = {
        username: userData.username.trim(),
        email: userData.email,
        profile_picture: tinyUrl || userData.profile_picture,
      };

      const response = await apiCall(
        "PUT",
        `/update-user/${userId}`,
        updateData,
        navigate
      );

      if (response && response.data) {
        const handleRes = response.data;
        handleApiResponse(handleRes, "success", "PUT");
        const updatedUser = {
          ...user,
          username: userData.username.trim(),
          profile_picture: tinyUrl || userData.profile_picture,
          role_name: userData.role_name, // Preserve role_name
        };
        localStorage.setItem("user", JSON.stringify(updatedUser));

        updateUserData({
          username: userData.username.trim(),
          profile_picture: tinyUrl || userData.profile_picture,
          role_name: userData.role_name,
        });

        if (tinyUrl) {
          setImagePreview(
            `https://pevbackend.codovio.com/public/uploads/profile_pictures/${tinyUrl}`
          );
        }

        navigate("/");
      }
    } catch (err) {
      setError(err.message || "Failed to update profile");

      const handleErr = error.response?.data;
      handleApiResponse(handleErr, "Err", "PUT");
    } finally {
      setUpdateLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "N/A";
    const names = name.split(" ");
    const initials = names.map((n) => n[0]).join("");
    return initials.toUpperCase();
  };

  if (loading) {
    return <LoadingComponent />;
  }

  if (error) {
    return <ErrorAlert error={error} />;
  }

  return (
    <div className="row gy-4">
      <div className="col">
        <div className="card h-100">
          <div className="card-body p-24">
            <ul
              className="nav border-gradient-tab nav-pills mb-20 d-inline-flex"
              id="pills-tab"
              role="tablist"
            >
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link d-flex align-items-center px-24 active"
                  id="pills-edit-profile-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-edit-profile"
                  type="button"
                  role="tab"
                  aria-controls="pills-edit-profile"
                  aria-selected="true"
                >
                  Edit Profile
                </button>
              </li>
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link d-flex align-items-center px-24"
                  id="pills-notification-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-notification"
                  type="button"
                  role="tab"
                  aria-controls="pills-notification"
                  aria-selected="false"
                  tabIndex={-1}
                >
                  Notification Settings
                </button>
              </li>
            </ul>
            <div className="tab-content" id="pills-tabContent">
              <div
                className="tab-pane fade show active"
                id="pills-edit-profile"
                role="tabpanel"
                aria-labelledby="pills-edit-profile-tab"
                tabIndex={0}
              >
                <div className="mb-24 mt-16">
                  <div className="avatar-upload">
                    <div className="avatar-preview position-relative">
                      <div
                        id="imagePreview"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "24px",
                          fontWeight: "bold",
                          color: "#666",
                          width: "100px",
                          height: "100px",
                          borderRadius: "50%",
                          border: "3px solid #e0e0e0",
                          cursor: "pointer",
                          overflow: "hidden",
                          backgroundColor: "#f8f9fa",
                          position: "relative",
                        }}
                        onClick={() =>
                          document.getElementById("imageUpload").click()
                        }
                      >
                        {imagePreview ? (
                          <img
                            src={imagePreview}
                            alt="Profile"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: "50%",
                            }}
                          />
                        ) : (
                          getInitials(userData.username)
                        )}
                      </div>
                      <div
                        className="position-absolute"
                        style={{
                          bottom: "0",
                          right: "0",
                          width: "32px",
                          height: "32px",
                          backgroundColor: "#475eab",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          border: "2px solid white",
                          boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                        }}
                        onClick={() =>
                          document.getElementById("imageUpload").click()
                        }
                      >
                        <Icon
                          icon="lucide:camera"
                          width="16"
                          height="16"
                          color="white"
                        />
                      </div>
                    </div>
                    <input
                      type="file"
                      id="imageUpload"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={{ display: "none" }}
                    />
                  </div>
                </div>
                <form onSubmit={handleUpdateUser}>
                  <div className="row">
                    <div className="col-sm-6">
                      <div className="mb-20">
                        <label
                          htmlFor="username"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          Username
                          <span className="text-danger-600">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control radius-8"
                          id="username"
                          placeholder="Enter Username"
                          value={userData.username}
                          onChange={handleUsernameChange}
                          maxLength={50}
                          required
                        />
                        <small className="text-muted">
                          {userData.username.length}/50 characters
                        </small>
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="mb-20">
                        <label
                          htmlFor="email"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          Email
                        </label>
                        <input
                          type="email"
                          className="form-control radius-8"
                          id="email"
                          placeholder="Email address"
                          value={userData.email}
                          readOnly
                          style={{ cursor: "not-allowed" }}
                        />
                      </div>
                    </div>
                    <div className="col-sm-6">
                      <div className="mb-20">
                        <label
                          htmlFor="role_name"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          Role
                        </label>
                        <input
                          type="text"
                          className="form-control radius-8"
                          id="role_name"
                          placeholder="Role"
                          value={userData.role_name}
                          readOnly
                          style={{ cursor: "not-allowed" }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-center gap-3">
                    <button
                      onClick={() => navigate("/")}
                      type="button"
                      className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                      disabled={updateLoading}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary-600 border border-primary-600 text-md px-56 py-12 radius-8"
                      disabled={updateLoading}
                    >
                      {updateLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Updating...
                        </>
                      ) : (
                        "Update Profile"
                      )}
                    </button>
                  </div>
                </form>
              </div>
              <div
                className="tab-pane fade"
                id="pills-notification"
                role="tabpanel"
                aria-labelledby="pills-notification-tab"
                tabIndex={0}
              >
                <div className="form-switch switch-primary py-12 px-16 border radius-8 position-relative mb-16">
                  <label
                    htmlFor="pushNotifcation"
                    className="position-absolute w-100 h-100 start-0 top-0"
                  />
                  <div className="d-flex align-items-center gap-3 justify-content-between">
                    <span className="form-check-label line-height-1 fw-medium text-secondary-light">
                      Push Notification
                    </span>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="pushNotifcation"
                      defaultChecked
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileLayer;
